import * as registrationActions from "@rentiohq/shared-frontend/dist/redux/registration/registration.actions";
import {
  getContractDocument,
  getLocationDescription,
} from "@rentiohq/shared-frontend/dist/redux/registration/registration.utils";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  ERegistrationDocumentStatus,
  ERegistrationContractType,
  ERegistrationDocumentType,
  IRegistration,
  ERegistrationBrusselsStatus,
  IRegistrationDocument,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { stringToSnakeCase } from "@rentiohq/shared-frontend/dist/utils/string.utils";
import { appendQueryParams } from "@rentiohq/shared-frontend/dist/utils/url.utils";
import { TextStyle } from "@rentiohq/web-shared/dist/components";
import { IAction } from "@rentiohq/web-shared/dist/types";
import { NavigateFunction } from "react-router-dom";

export const getActivitiesFilter = (registration: IRegistration) => {
  const { id: rentRegistrationId } = registration;
  return {
    where: { rentRegistrationId },
  };
};

export const getContractActions = (params: {
  registration: IRegistration;
  property?: IProperty;
  navigate: NavigateFunction;
  dispatch: any;
  onDrawerClose?: () => void;
}) => {
  const { registration, property, navigate, dispatch, onDrawerClose } = params;

  const document = getContractDocument(registration);
  if (!document) {
    return [];
  }

  const actions: IAction[] = [];

  if (document.status === ERegistrationDocumentStatus.Registered) {
    actions.push({
      content: (
        <TextStyle>{getLocalizedText("registration.addendum.title")}</TextStyle>
      ),
      onClick: () => {
        navigate(
          `/properties/${property?.id}/contracts/${registration?.contractId}/registration?id=${registration.id}&documentType=${ERegistrationDocumentType.Addendum}`,
        );
      },
    });
  }

  if (document.status === ERegistrationDocumentStatus.Failed) {
    actions.push({
      content: (
        <TextStyle variation={"negative"}>
          {getLocalizedText("system.delete")}
        </TextStyle>
      ),
      onClick: () => {
        dispatch(
          registrationActions.deleteRegistration.actions.start({
            id: registration.id,
            onSuccess: () => {
              onDrawerClose?.();
            },
          }),
        );
      },
    });
  }

  return actions;
};

export const getLocationDescriptionActions = (params: {
  registration: IRegistration;
  property?: IProperty;
  navigate: NavigateFunction;
  dispatch: any;
}) => {
  const { registration, navigate, dispatch } = params;

  const { locationDescriptionStatus } = registration;

  const document = getLocationDescription(registration);
  const contractDocument = getContractDocument(registration);

  const status = document?.status || locationDescriptionStatus;

  const actionAddRegistration: IAction = {
    content: (
      <TextStyle variation={"positive"}>
        {getLocalizedText("registration.detail.cta.add_location_description")}
      </TextStyle>
    ),
    onClick: () => {
      navigate(
        `/properties/${registration.propertyId}/contracts/${registration.contractId}/registration?id=${registration.id}&documentType=${ERegistrationDocumentType.PropertyInspectionReport}&existingDocument=${document?.id}`,
      );
    },
  };

  const getMarkAsAction = (status: ERegistrationDocumentStatus) => {
    const result: IAction = {
      content: (
        <TextStyle variation={"default"}>
          {getLocalizedText("system.mark_entity_as", {
            entity: getLocalizedText(
              "system.location_description",
            ).toLowerCase(),
            value: getLocalizedText(
              `registration.status.${stringToSnakeCase(status)}`.toLowerCase(),
            ),
          })}
        </TextStyle>
      ),
      onClick: () => {
        if (document) {
          dispatch(
            registrationActions.registrationUpdateDocument.actions.start({
              registrationId: registration.id,
              documentId: document.id,
              document: {
                status,
              },
            }),
          );

          return;
        }

        const locationDescriptionDocument = {
          documentId: undefined,
          documentType: ERegistrationDocumentType.PropertyInspectionReport,
          type: ERegistrationContractType.Amendment,
          signedAt: new Date(),
          signers: [],
          status,
        };

        dispatch(
          registrationActions.registrationAddDocuments.actions.start({
            registrationId: registration.id,
            documents: [locationDescriptionDocument],
          }),
        );
      },
    };

    return result;
  };

  const actionMarkRegisteredManually = getMarkAsAction(
    ERegistrationDocumentStatus.RegisteredManually,
  );
  const actionMarkNone = getMarkAsAction(ERegistrationDocumentStatus.None);
  const actionMarkNotApplicable = getMarkAsAction(
    ERegistrationDocumentStatus.NotApplicable,
  );

  const result: IAction[] = [];

  if (
    // Registering a property inspection report without a barcode from the (pending) contract will fail
    !!contractDocument?.barCode &&
    [
      ERegistrationDocumentStatus.None,
      ERegistrationDocumentStatus.Rejected,
      ERegistrationDocumentStatus.Failed,
    ].includes(status)
  ) {
    result.push(actionAddRegistration);
  }

  if (
    [
      ERegistrationDocumentStatus.RegisteredManually,
      ERegistrationDocumentStatus.NotApplicable,
      ERegistrationDocumentStatus.Rejected,
      ERegistrationDocumentStatus.Failed,
    ].includes(status)
  ) {
    result.push(actionMarkNone);
  }

  if (
    [
      ERegistrationDocumentStatus.None,
      ERegistrationDocumentStatus.Rejected,
      ERegistrationDocumentStatus.Failed,
    ].includes(status) &&
    actionMarkRegisteredManually
  ) {
    result.push(actionMarkRegisteredManually);
    result.push(actionMarkNotApplicable);
  }

  if (status === ERegistrationDocumentStatus.RegisteredManually) {
    result.push(actionMarkNotApplicable);
  }

  if (status === ERegistrationDocumentStatus.NotApplicable) {
    result.push(actionMarkRegisteredManually);
  }

  return result;
};

export const getBrusselsActions = (params: {
  registration: IRegistration;
  property?: IProperty;
  navigate: NavigateFunction;
  dispatch: any;
}) => {
  const { registration, navigate, dispatch } = params;

  const { brusselsStatus = ERegistrationBrusselsStatus.None } = registration;

  const actionSendToBrussels: IAction = {
    content: (
      <TextStyle variation={"positive"}>
        {getLocalizedText("registration.detail.cta.send_to_brussels")}
      </TextStyle>
    ),
    onClick: () => {
      navigate(
        appendQueryParams({
          path: `/properties/${registration.propertyId}/contracts/${registration.contractId}/registration`,
          queryParams: {
            id: registration.id,
            documentType: ERegistrationDocumentType.Brussels,
          },
        }),
      );
    },
  };

  const getMarkAsAction = (status: ERegistrationBrusselsStatus) => {
    const result: IAction = {
      content: (
        <TextStyle variation={"default"}>
          {getLocalizedText("system.mark_entity_as", {
            entity: getLocalizedText(
              "system.location_description",
            ).toLowerCase(),
            value: getLocalizedText(
              `registration.status.${stringToSnakeCase(status)}`.toLowerCase(),
            ),
          })}
        </TextStyle>
      ),
      onClick: () => {
        dispatch(
          registrationActions.updateRegistration.actions.start({
            id: registration.id,
            registration: {
              brusselsStatus: status,
            },
          }),
        );
      },
    };

    return result;
  };

  const actionMarkRegisteredManually = getMarkAsAction(
    ERegistrationBrusselsStatus.RegisteredManually,
  );
  const actionMarkNone = getMarkAsAction(ERegistrationBrusselsStatus.None);

  const result: IAction[] = [];

  if (
    [
      ERegistrationBrusselsStatus.None,
      ERegistrationBrusselsStatus.Failed,
    ].includes(brusselsStatus)
  ) {
    result.push(actionSendToBrussels);
  }

  if (
    [
      ERegistrationBrusselsStatus.RegisteredManually,
      ERegistrationBrusselsStatus.Failed,
    ].includes(brusselsStatus)
  ) {
    result.push(actionMarkNone);
  }

  if (
    [
      ERegistrationBrusselsStatus.None,
      ERegistrationBrusselsStatus.Failed,
    ].includes(brusselsStatus)
  ) {
    result.push(actionMarkRegisteredManually);
  }

  return result;
};

export const getAddendumActions = (params: {
  registration: IRegistration;
  document: IRegistrationDocument;
  dispatch: any;
  onDrawerClose?: () => void;
}) => {
  const { registration, dispatch, document, onDrawerClose } = params;

  const actions: IAction[] = [];

  if (
    [
      ERegistrationDocumentStatus.Rejected,
      ERegistrationDocumentStatus.Failed,
    ].includes(document.status)
  ) {
    actions.push({
      content: (
        <TextStyle variation={"negative"}>
          {getLocalizedText("system.delete")}
        </TextStyle>
      ),
      onClick: () => {
        dispatch(
          registrationActions.registrationDeleteDocument.actions.start({
            registrationId: registration.id,
            documentId: document.id,
            onSuccess: () => {
              onDrawerClose?.();
            },
          }),
        );
      },
    });
  }

  return actions;
};
